import React, { useEffect, useMemo } from 'react';
import './style.css';

const ensureYotpoReady = () =>
  new Promise((resolve) => {
    // already ready
    if (typeof window !== 'undefined' && window.yotpo?.refreshWidgets) {
      resolve();
      return;
    }
    // ensure the script exists once
    const existing = document.getElementById('yotpo-widget-script');
    if (!existing) {
      const s = document.createElement('script');
      s.id = 'yotpo-widget-script';
      s.type = 'text/javascript';
      s.async = true;
      s.src =
        'https://staticw2.yotpo.com/MtI6WMUc1SMDm8DW3ZmRDSTA4TUsLomtzi81ZY7C/widget.js';
      document.head.appendChild(s);
    }
    // poll until window.yotpo.refreshWidgets is available
    const start = Date.now();
    const tick = () => {
      if (window.yotpo?.refreshWidgets) return resolve();
      if (Date.now() - start > 8000) return resolve(); // give up gracefully
      setTimeout(tick, 100);
      return false;
    };
    tick();
  });

const ensureDOMElementExists = (elementId, maxWait = 3000) =>
  new Promise((resolve) => {
    const element = document.getElementById(elementId);
    if (element) {
      resolve(element);
      return;
    }

    const start = Date.now();
    const tick = () => {
      if (window.yotpo?.refreshWidgets) {
        resolve();
        return;
      }
      if (Date.now() - start > maxWait) {
        resolve();
        return;
      }
      setTimeout(tick, 100);
    };

    tick();
  });

const ProductReviewRating = ({ product }) => {
  // resolve the external_id Yotpo expects
  const yotpoProductId = useMemo(() => {
    const id = product?.subscriptionPlan?.yotpoSku ?? product?.sku ?? '';
    return id?.toString().trim() || '';
  }, [product?.subscriptionPlan?.yotpoSku, product?.sku]);

  const name = product?.name || '';
  const url = product?.urlName
    ? `https://bumpboxes.com/products/${product.urlName.toLowerCase()}`
    : '';
  const imageUrl = product?.media?.[0]?.url || '';

  // Only initialize if we have a valid product ID
  const shouldInitialize = Boolean(yotpoProductId && name);

  // Refresh AFTER the node is in the DOM and Yotpo is ready.
  useEffect(() => {
    if (!shouldInitialize) return;

    let cancelled = false;
    let timeoutId;

    const run = async () => {
      await ensureYotpoReady();
      const element = await ensureDOMElementExists('pdpReviewRating');

      if (!cancelled && element && window.yotpo?.refreshWidgets) {
        // Clear any existing Yotpo widgets on this element first
        const existingWidget = element.querySelector('.yotpo-widget');
        if (existingWidget) {
          existingWidget.remove();
        }

        // Use a longer delay and ensure all data attributes are set
        timeoutId = setTimeout(() => {
          if (!cancelled && document.getElementById('pdpReviewRating')) {
            try {
              window.yotpo.refreshWidgets();
            } catch (refreshError) {
              // Silently handle refresh errors to prevent console spam
            }
          }
        }, 250);
      }
    };

    run();
    // Cleanup function for useEffect
    (() => {
      cancelled = true;
      if (timeoutId) clearTimeout(timeoutId);
    })();
  }, [shouldInitialize, yotpoProductId]);

  // Don't render if we don't have the minimum required data
  if (!shouldInitialize) {
    return null;
  }

  return (
    <div
      id='pdpReviewRating'
      className='productReviewRating yotpo bottomLine'
      data-yotpo-product-id={yotpoProductId}
      data-yotpo-name={name}
      data-yotpo-url={url}
      data-yotpo-image-url={imageUrl}
    />
  );
};

export default React.memo(ProductReviewRating);
